import React, { useMemo } from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useIndent } from "./precallLayout"

/**
 * Ce que l'élève va voir s'ouvrir quand il aura trouvé l'icône : les deux
 * appareils, et « Autoriser » à choisir pour chacun.
 *
 * On dessine le résultat attendu, pas l'endroit où cliquer. Les navigateurs
 * déplacent leurs boutons d'une version à l'autre ; un schéma qui montrait
 * l'icône « à droite de l'adresse » est devenu faux sur Chrome sans prévenir.
 * La fenêtre d'autorisation, elle, se ressemble partout : deux lignes, deux
 * choix. L'appareil de l'étape en cours est mis en avant.
 */
export const PermissionHint = ({ device }) => (
    <View
        style={{
            alignSelf: "flex-start",
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme.colors.lightMore,
            backgroundColor: theme.colors.white,
            paddingVertical: 9,
            paddingHorizontal: 11,
        }}
    >
        {["Micro", "Caméra"].map((name) => {
            const active = name === device
            return (
                <View
                    key={name}
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginTop: name === "Micro" ? 0 : 8,
                    }}
                >
                    <TextM
                        color={active ? theme.colors.black : theme.colors.light}
                        fontSize={11}
                        fontWeight={active ? "Bold" : "Regular"}
                        style={{ width: 52 }}
                    >
                        {name}
                    </TextM>
                    <View
                        style={{
                            borderRadius: 999,
                            backgroundColor: active ? theme.colors.primary : theme.colors.lighter,
                            paddingVertical: 3,
                            paddingHorizontal: 10,
                        }}
                    >
                        <TextM
                            color={active ? theme.colors.white : theme.colors.light}
                            fontSize={10.5}
                            fontWeight={"Bold"}
                        >
                            Autoriser
                        </TextM>
                    </View>
                    <TextM color={theme.colors.lightMore} fontSize={10.5} style={{ marginLeft: 10 }}>
                        Bloquer
                    </TextM>
                </View>
            )
        })}
    </View>
)

/**
 * Le guidage d'une étape : un geste par ligne, affiché d'un bloc.
 *
 * Plus d'écriture lettre à lettre : un élève de dix ans lit lentement, et une
 * phrase qui s'écrit devant lui le fait attendre avant de pouvoir agir. Plus
 * de compteur « Essai n sur m » non plus : il annonçait combien d'échecs
 * restaient à venir.
 *
 * `illustration` : un schéma rattaché à UNE ligne, rendu juste en dessous
 * d'elle.
 */
const TypedGuidance = ({ lines, dotColor, illustration }) => {
    const indent = useIndent()
    // Un geste est soit une phrase nue, soit une phrase et son icône.
    const items = useMemo(
        () =>
            (Array.isArray(lines) ? lines : [lines]).map((line) =>
                typeof line === "string" ? { text: line } : line
            ),
        [lines]
    )

    return (
        <View
            style={{
                marginLeft: indent,
                marginTop: 14,
                backgroundColor: theme.colors.white,
                borderRadius: 14,
                borderWidth: 1,
                borderColor: theme.colors.lighter,
                paddingVertical: 13,
                paddingHorizontal: 15,
            }}
        >
            {items.map((line, index) => {
                const row = (
                    <View
                        key={line.text}
                        style={{ flexDirection: "row", marginTop: index === 0 ? 0 : 11 }}
                    >
                        {line.icon ? (
                            <View
                                style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 7,
                                    backgroundColor: theme.colors.primaryLighter,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: 11,
                                }}
                            >
                                <FontAwesome5 name={line.icon} size={11} color={theme.colors.primary} />
                            </View>
                        ) : (
                            items.length > 1 && (
                                <View
                                    style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: dotColor,
                                        marginTop: 8,
                                        marginRight: 11,
                                    }}
                                />
                            )
                        )}
                        <TextM
                            color={theme.colors.black}
                            fontSize={15}
                            style={{ flex: 1, lineHeight: 22 }}
                            wrap
                        >
                            {line.text}
                        </TextM>
                    </View>
                )

                if (!illustration || illustration.afterIndex !== index) return row
                return (
                    <View key={line.text}>
                        {row}
                        {/* Aligné sur le texte du conseil, pas sur sa pastille :
                            le schéma prolonge la phrase. */}
                        <View style={{ marginTop: 9, marginLeft: 35 }}>{illustration.node}</View>
                    </View>
                )
            })}
        </View>
    )
}

export default TypedGuidance
